import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@naf-simba/elements-buttons';
import { InternalLinkType } from '@appTypes/internalLinkType';
import { useDocumentUrlWithNode } from '@hooks/Documents/useDocumentUrl';
import { useTrackCTA } from '@utils/Tracking/CtaClick/TrackCtaClick';
import { useTrackPromotion } from '@hooks/useTrackPromotion';
import { getPromotionEventTrackingData } from '@utils/Tracking/Promotion/getPromotionEventTrackingData';
import { mapSimbaVariant, type LegacyButtonVariant } from '@utils/simba';

interface CtaButtonBodySerializerProps {
  value: {
    text: string;
    url: string;
    blank?: boolean;
    variant?: LegacyButtonVariant;
    internalLink?: InternalLinkType;
  };
}

export const CtaButtonBody = ({ value: { text, url, variant, blank, internalLink } }: CtaButtonBodySerializerProps) => {
  const trackCTA = useTrackCTA();
  const navigate = useNavigate();
  const isInternal = useDocumentUrlWithNode(internalLink);
  const urlToCheck = isInternal || url;
  const buttonVariant = mapSimbaVariant(variant);
  const promotion = getPromotionEventTrackingData(text, urlToCheck);
  const shouldTrackPromotion = Boolean(promotion);
  const { wrapperRef, trackSelectPromotion } = useTrackPromotion({
    creative_slot: 'block-content',
    creative_name: `CTA | ${variant} | ${text}`,
    promotion_id: promotion?.promotion_id,
    promotion_name: promotion?.promotion_name,
  });

  const handleTracking = (buttonText: string, path: string) => {
    trackCTA({
      cta_position: 'content',
      text: buttonText,
      url: path,
    });
  };

  const handleInternalLink = (buttonText: string, path: string) => {
    if (shouldTrackPromotion) {
      trackSelectPromotion();
    }
    handleTracking(buttonText, path);
    navigate(path);
  };

  const handleExternalButtonClick = () => {
    if (shouldTrackPromotion) {
      trackSelectPromotion();
    }
    handleTracking(text, url);
  };

  if (isInternal) {
    return (
      <div ref={shouldTrackPromotion ? wrapperRef : undefined}>
        <Button
          variant={buttonVariant.variant}
          color={buttonVariant.color}
          shade={buttonVariant.shade}
          onClick={() => handleInternalLink(text, isInternal)}
        >
          {text}
        </Button>
      </div>
    );
  }

  return (
    <div ref={shouldTrackPromotion ? wrapperRef : undefined}>
      <Button
        color={buttonVariant.color}
        shade={buttonVariant.shade}
        href={url}
        target={blank ? '_blank' : '_self'}
        rel="noopener noreferrer"
        onClick={handleExternalButtonClick}
      >
        {text}
      </Button>
    </div>
  );
};
